import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from 'api/index';

// Fetch folder tree async thunk
export const fetchFolderTree = createAsyncThunk(
  'folders/fetchFolderTree',
  async (_, { rejectedWithValue }) => {
    try {
      const response = await API.get('/folders/tree');
      return response.data.folderTree;
    } catch (error) {
      return rejectedWithValue(error.response?.data?.message || 'Failed to fetch folder tree');
    }
  }
);

// Add folder async thunk
export const addFolder = createAsyncThunk(
  'folders/addFolder',
  async ({ name, parentFolderId }, { rejectWithValue }) => {
    try {
      const response = await API.post('/folders', { name, parentFolder: parentFolderId });
      return { newFolder: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add folder');
    }
  }
);

// Delete folder async thunk
export const deleteFolder = createAsyncThunk(
  'folders/deleteFolder',
  async (folderId, { rejectedWithValue }) => {
    try {
      await API.delete(`/folders/${folderId}`);
      return folderId;
    } catch (error) {
      return rejectedWithValue(error.response?.data?.message || 'Failed to delete folder');
    }
  }
);

// Rename folder async thunk
export const renameFolder = createAsyncThunk(
  'folders/renameFolder',
  async ({ folderId, name }, { rejectedWithValue }) => {
    try {
      const response = await API.put(`/folders/${folderId}`, { name });
      return response.data;
    } catch (error) {
      return rejectedWithValue(error.response?.data?.message || 'Failed to rename folder');
    }
  }
);

// Update folder data
export const updateFolderData = createAsyncThunk(
  'folders/updateFolderData',
  async ({ folderId, data }, { rejectWithValue }) => {
    try {
      const response = await API.put(`/folders/${folderId}/addImage`, data);
      return { folderId, image: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update folder');
    }
  }
);




// Slice definition
const folderSlice = createSlice({
  name: 'folders',
  initialState: {
    folderTree: [],
    openedFolderID: null,
    loading: false,
    status: 'idle',
    error: null,
  },
  reducers: {
    setFolderTree: (state, action) => {
      state.folderTree = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Featch Folder
      .addCase(fetchFolderTree.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFolderTree.fulfilled, (state, action) => {
        state.loading = false;
        state.folderTree = action.payload;
      })
      .addCase(fetchFolderTree.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add folder
      .addCase(addFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(addFolder.fulfilled, (state, action) => {
        const { newFolder } = action.payload;
      
        const updateFolderTree = (folders, newFolder) => {
          return folders.map((folder) => {
            if (folder._id === newFolder.parentFolder) {
              return {
                ...folder,
                children: [...(folder.children || []), newFolder],
              };
            } else if (folder.children?.length > 0) {
              return {
                ...folder,
                children: updateFolderTree(folder.children, newFolder),
              };
            }
            return folder;
          });
        };
      
        state.folderTree = updateFolderTree(state.folderTree, newFolder);
      })
      
      
      

      .addCase(addFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Folder
      .addCase(deleteFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        const folderIdToDelete = action.payload;
      
        // ✅ Recursive function to remove the deleted folder from nested structure
        const removeFolder = (folders, folderId) => {
          return folders
            .filter(folder => folder._id !== folderId)
            .map(folder => ({
              ...folder,
              children: removeFolder(folder.children || [], folderId),
            }));
        };
      
        state.folderTree = removeFolder(state.folderTree, folderIdToDelete);
      })
          
      .addCase(deleteFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // rename folder
      .addCase(renameFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(renameFolder.fulfilled, (state, action) => {
        const updatedFolder = action.payload;
        state.folderTree = state.folderTree.map((folder) =>
          folder._id === updatedFolder._id ? updatedFolder : folder
        );
        state.loading = false;
      })
      .addCase(renameFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // update Folder
      .addCase(updateFolderData.fulfilled, (state, action) => {
        const { folderId, image } = action.payload;
        const updateFolderImages = (folders) =>
          folders.map((folder) => {
            if (folder._id === folderId) {
              return { ...folder, images: [...(folder.images || []), image] };
            }
            if (folder.children) {
              return { ...folder, children: updateFolderImages(folder.children)};
            }
            return folder;
          });
          state.folderTree = updateFolderImages(state.folderTree);
      })
  },
});

export default folderSlice.reducer;
